/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaPrompt } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';

/**
 * Returns the pairs with the unsaved changes with the valid data structure for the backend to be processed.
 * e.g.: Adding new qna through follow-up prompt should be nested inside the parent not flattened like the others.
 */
export const getNestedDirtyPairs = (dirtyPairs: QnaPatchOperation<QnaMakerQnaSwagger>[]) => {
    const visitedPairIds: number[] = [];

    const getNestedPrompt = (prompt: QnaMakerQnaPrompt): QnaMakerQnaPrompt => {
        if (!prompt.qna) {
            return prompt;
        }

        const promptQnaPair = dirtyPairs.find(p => p.value.id === prompt.qna.id)?.value || prompt.qna;

        visitedPairIds.push(promptQnaPair.id);

        const qna = <QnaMakerQnaSwagger>{
            ...promptQnaPair,
            parentQnaIds: undefined,
            dialog: promptQnaPair?.dialog && {
                ...promptQnaPair.dialog,
                prompts: [...promptQnaPair.dialog.prompts].map(getNestedPrompt)
            }
        };

        return { ...prompt, qna: qna.id ? { ...qna, id: undefined } : undefined, qnaId: undefined };
    };

    return dirtyPairs
        .map(pair => ({
            op: pair.op,
            value: {
                ...pair.value,
                parentQnaIds: undefined,
                dialog: {
                    ...pair.value.dialog,
                    prompts: [...pair.value.dialog.prompts].map(getNestedPrompt)
                }
            }
        }))
        .filter(p => !visitedPairIds.includes(p.value.id));
};
